//import injectionTarget from "../core/di/DecoratorInjectionTarget";
import type ILedgerRepository from "../repositories/interfaces/ILedgerRepository";
import Container from "../core/di/DependencyContainer.ts";
import BaseService, {RequestError, RequestState} from "./BaseService.ts";
import Currency from "../libs/ledger/Currency.ts";
import {Balance} from "../repositories/dto/ledger/balance/Balance.ts";
import baseCurrencies from "../libs/ledger/BaseCurrencies.ts";

export type GetTransactionsParams = {
    limit?: number;
    page?: number;
    accountId?: string;
    orderBy?: string;
};

export type GetTransactionHistoryParams = {
    accountId?: string;
    startDate?: Date;
    endDate?: Date;
    transactionType?: string;
    status?: string;
    page?: number;
    limit?: number;
};

export type GetAccountsParams = {
    type?: "fiat" | "crypto";
    full?: boolean;
};

export type GetCurrenciesByCodeParams = {
    currencies?: string[];
};

export type CryptoTicker = {
    code: string;
    value: number;
    change: number;
};

export type TransactionShort = {};

export type List<T> = {
    list: T[];
    meta: {
        records: number;
        currentPage: number;
        lastPage: number;
        minEntry: number;
        maxEntry: number;
    };
};

//@injectionTarget()
export default class LedgerService extends BaseService {
    ledgerRepository: ILedgerRepository;

    constructor(
        //@inject("ledgerRepository") ledgerRepository?: ILedgerRepository
    ) {
        super();
        let ledgerRepository: ILedgerRepository | null = Container.get("ledgerRepository");
        // ensures userRepository exists and no checks for undefined are required throughout the class
        if (ledgerRepository == null) {
            throw Error("No LedgerRepository provided or injected.");
        }
        this.ledgerRepository = ledgerRepository;
    }

    async getBalances(inCurrency?: string) {
        const balances = await this.ledgerRepository.getTotalBalances(inCurrency ?? 'EUR');
        return balances;
    }

    async getTransactions(
        params: GetTransactionsParams
    ): Promise<List<TransactionShort>> {
        const list = await this.ledgerRepository.getTransactions({
            accountId: params.accountId,
            limit: params.limit,
            page: params.page,
            orderBy: params.orderBy
        });
        return {
            list: list.list,
            meta: list.meta
        };
    }

    async getTransactionHistory(
        params: GetTransactionHistoryParams
    ): Promise<List<TransactionShort>> {
        const list = await this.ledgerRepository.getTransactionHistory({
            accountId: params.accountId,
            startDate: params.startDate,
            endDate: params.endDate,
            transactionType: params.transactionType,
            status: params.status,
            page: params.page,
            limit: params.limit
        });
        return {
            list: list.list,
            meta: list.meta
        };
    }

    async getAccounts(/*params: GetAccountsParams*/) {
    }

    async getCryptoTicker(cryptoCode: string): Promise<CryptoTicker> {
        return new Promise((resolve, _) => {
            const ticker: CryptoTicker = {
                code: cryptoCode,
                value: 530000.0,
                change: 3.57
            };
            setTimeout(() => resolve(ticker), 3000);
        });
    }

    async getTotalCryptoBalance(refKey: string, currency: Currency): Promise<void> {
        this.updateRefState(refKey, RequestState.Loading);
        try {
            const balance = await this.ledgerRepository.getTotalBalance('crypto', currency.code);
            const value: Balance = {
                available: balance.availableAmount,
                currency: baseCurrencies.codeToCurrency(balance.currencyCode),
                onhold: balance.onholdAmount,
                total: balance.totalAmount,
            }

            this.updateRefValue(refKey, value);
            this.updateRefState(refKey, RequestState.Ready);
        } catch (e) {
            this.updateRefError(refKey, new RequestError((e as any).code, (e as any).message));
            this.updateRefState(refKey, RequestState.Failed);
        }
    }

    async topUpAccount(params: {
        toAccount: string;
        amount: number;
        currency: string;
        paymentMethod: string;
        paymentDetails: any;
    }): Promise<{ transactionId: string; topUpId: string }> {
        return await this.ledgerRepository.topUpAccount(params);
    }

    async sendCrypto(params: {
        fromAccount: string;
        toAddress: string;
        amount: number;
        currency: string;
        networkFee: number;
        memo?: string;
    }): Promise<{ transactionHash: string; transactionId: string }> {
        return await this.ledgerRepository.sendCrypto(params);
    }

    async buyCrypto(params: {
        cryptocurrency: string;
        amount: number;
        paymentMethod: string;
        fiatAmount: number;
    }): Promise<{ transactionId: string; purchaseId: string }> {
        return await this.ledgerRepository.buyCrypto(params);
    }

    async exchangeCrypto(params: {
        fromCurrency: string;
        toCurrency: string;
        fromAmount: number;
        toAmount: number;
        slippageTolerance: number;
    }): Promise<{ transactionHash: string; exchangeId: string }> {
        return await this.ledgerRepository.exchangeCrypto(params);
    }

    async sendCash(params: {
        fromAccount: string;
        recipientName: string;
        recipientType: 'bank' | 'email' | 'phone';
        recipientDetails: any;
        amount: number;
        currency: string;
        transferMethod: string;
        memo?: string;
    }): Promise<{ transactionId: string; transferId: string }> {
        return await this.ledgerRepository.sendCash(params);
    }

    async orderCard(params: {
        cardType: 'virtual' | 'physical' | 'premium';
        cardholderName: string;
        linkedAccount: string;
        design?: string;
        shippingAddress?: any;
        settings: any;
    }): Promise<{ orderNumber: string; cardId: string }> {
        return await this.ledgerRepository.orderCard(params);
    }
}
